import React, { useCallback, useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AuthContext from "../auth/AuthContext";
import {
  createPostComment,
  createRecipeComment,
  deleteComment,
  listPostComments,
  listRecipeComments,
  updateComment,
} from "../utils/apiClient";

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function CommentItem({
  comment,
  depth = 0,
  onReply,
  onEdit,
  onDelete,
  busyId,
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(comment.body);
  const busy = busyId === comment.comment_uuid;

  const submitReply = async () => {
    const body = replyText.trim();
    if (!body) return;
    await onReply(comment.comment_uuid, body);
    setReplyText("");
    setReplyOpen(false);
  };

  const submitEdit = async () => {
    const body = editText.trim();
    if (!body) return;
    await onEdit(comment.comment_uuid, body);
    setEditing(false);
  };

  return (
    <Box
      sx={{
        pl: depth ? 2 : 0,
        borderLeft: depth ? "2px solid" : "none",
        borderColor: "divider",
        py: 1.25,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {comment.author?.name || "Bruger"}
        {comment.created_at ? ` · ${formatDate(comment.created_at)}` : ""}
        {comment.edited_at ? " · redigeret" : ""}
      </Typography>
      {editing ? (
        <Stack spacing={1} sx={{ mt: 0.75 }}>
          <TextField
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={busy}
          />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" disabled={busy} onClick={submitEdit}>
              Gem
            </Button>
            <Button
              size="small"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setEditText(comment.body);
              }}
            >
              Annuller
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {comment.body}
        </Typography>
      )}
      {!editing ? (
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          {comment.can_reply ? (
            <Button size="small" onClick={() => setReplyOpen((v) => !v)} disabled={busy}>
              Svar
            </Button>
          ) : null}
          {comment.can_edit ? (
            <Button
              size="small"
              onClick={() => {
                setEditText(comment.body);
                setEditing(true);
              }}
              disabled={busy}
            >
              Rediger
            </Button>
          ) : null}
          {comment.can_edit ? (
            <Button
              size="small"
              color="error"
              onClick={() => onDelete(comment.comment_uuid)}
              disabled={busy}
            >
              Slet
            </Button>
          ) : null}
        </Stack>
      ) : null}
      {replyOpen ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <TextField
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Skriv et svar…"
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={busy}
          />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" disabled={busy || !replyText.trim()} onClick={submitReply}>
              Post svar
            </Button>
            <Button size="small" disabled={busy} onClick={() => setReplyOpen(false)}>
              Annuller
            </Button>
          </Stack>
        </Stack>
      ) : null}
      {(comment.replies || []).map((reply) => (
        <CommentItem
          key={reply.comment_uuid}
          comment={reply}
          depth={depth + 1}
          onReply={onReply}
          onEdit={onEdit}
          onDelete={onDelete}
          busyId={busyId}
        />
      ))}
    </Box>
  );
}

/**
 * Comment thread for a post or recipe. Only rendered for signed-in users.
 */
export function CommentsSection({ kind, contentRef }) {
  const { user } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    if (!user || !contentRef) {
      setComments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data =
        kind === "recipe"
          ? await listRecipeComments(contentRef)
          : await listPostComments(contentRef);
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [kind, contentRef, user?.user_uuid]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user) return null;

  const handleCreate = async () => {
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    setError("");
    try {
      if (kind === "recipe") {
        await createRecipeComment(contentRef, { body });
      } else {
        await createPostComment(contentRef, { body });
      }
      setDraft("");
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const handleReply = async (parentCommentUuid, body) => {
    setBusyId(parentCommentUuid);
    setError("");
    try {
      const payload = { body, parent_comment_uuid: parentCommentUuid };
      if (kind === "recipe") {
        await createRecipeComment(contentRef, payload);
      } else {
        await createPostComment(contentRef, payload);
      }
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to reply");
      throw err;
    } finally {
      setBusyId(null);
    }
  };

  const handleEdit = async (commentUuid, body) => {
    setBusyId(commentUuid);
    setError("");
    try {
      await updateComment(commentUuid, { body });
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to edit comment");
      throw err;
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (commentUuid) => {
    if (!window.confirm("Slet denne kommentar?")) return;
    setBusyId(commentUuid);
    setError("");
    try {
      await deleteComment(commentUuid);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to delete comment");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, letterSpacing: "-0.02em" }}>
        Kommentarer
      </Typography>

      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Skriv en kommentar"
          multiline
          minRows={2}
          fullWidth
          size="small"
          disabled={posting}
        />
        <Box>
          <Button
            variant="contained"
            size="small"
            disabled={posting || !draft.trim()}
            onClick={handleCreate}
          >
            {posting ? "Poster…" : "Post kommentar"}
          </Button>
        </Box>
      </Stack>

      {error ? (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : comments.length ? (
        <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
          {comments.map((c) => (
            <CommentItem
              key={c.comment_uuid}
              comment={c}
              onReply={handleReply}
              onEdit={handleEdit}
              onDelete={handleDelete}
              busyId={busyId}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Ingen kommentarer endnu
        </Typography>
      )}
    </Box>
  );
}

export default CommentsSection;
